import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useFieldArray, useForm, useWatch, type Control, type UseFormRegister, type UseFormSetValue } from 'react-hook-form';
import { Copy, Plus, RotateCcw, Save, ShieldAlert, Trash2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  DateField,
  ErrorState,
  FormActions,
  FormField,
  FormGrid,
  IconButton,
  Loading,
  Modal,
  NumberField,
  PageHeader,
  Panel,
  RadioGroup,
  StatusBadge,
  Textarea,
  TextField,
  useConfirm,
  useToast,
} from '@/components';
import { useCurrentUser } from '@/auth/AuthProvider';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { BusinessOperatorSelect, PortSelect, useCodeValues, VesselSelect } from '@/features/masters';
import { ApiError, errorMessage } from '@/lib/api';
import { applyApiErrors } from '@/lib/form';
import { changeTypeLabel, useNotice, useSaveNotice, validateNotice, type Confirmation, type Issue, type NoticeDetail, type ValidateResult } from './api';
import { IssueList } from './components/IssueList';
import { emptyNoticeForm, emptyVesselRow, noticeFormFromDetail, toNoticeInput, type NoticeForm } from './form';
import './advance-notices.css';
import { ControlledSelect } from './components/ControlledSelect';

/** S-21 事前連絡 登録・変更（/advance-notices/new, /advance-notices/:id/edit） */
export function AdvanceNoticeFormPage() {
  const { id } = useParams();
  const noticeId = id ? Number(id) : null;
  const { data, isPending, error, refetch } = useNotice(noticeId);
  if (noticeId != null) {
    if (isPending) return <Loading block />;
    if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  }
  return <NoticeFormBody key={noticeId ?? 'new'} detail={noticeId != null ? data : undefined} />;
}

const options = (list: readonly string[] | undefined) => (list ?? []).map((v) => ({ value: v, label: v }));

function NoticeFormBody({ detail }: { detail?: NoticeDetail }) {
  const isNew = !detail;
  const user = useCurrentUser();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { year, yearPath } = useFiscalYear();
  const save = useSaveNotice(detail?.id ?? null);
  const ownOp = user.organizationType === '漁協' && user.businessOperatorId != null ? Number(user.businessOperatorId) : null;
  const form = useForm<NoticeForm>({ defaultValues: detail ? noticeFormFromDetail(detail) : emptyNoticeForm(year, ownOp) });
  const { register, control, handleSubmit, setError, setValue, formState } = form;
  const rows = useFieldArray({ control, name: 'vessels' });
  const headerOp = useWatch({ control, name: 'businessOperatorId' });
  const regType = useWatch({ control, name: 'registrationType' });
  const [issues, setIssues] = useState<Issue[]>([]);
  const [denial, setDenial] = useState<ValidateResult['denialReasons'] | null>(null);
  const [pending, setPending] = useState<{ list: Confirmation[]; answers: Record<string, boolean> } | null>(null);
  const [busy, setBusy] = useState(false);

  /** 検証 → 確認 → 否認表示 → 保存 */
  const run = async (values: NoticeForm, answers: Record<string, boolean>) => {
    setBusy(true);
    setIssues([]);
    try {
      const input = toNoticeInput(values, answers, detail?.id);
      const v = await validateNotice(input);
      if (v.errors.length) {
        setIssues(v.errors);
        v.errors.forEach((e) => e.path && setError(e.path as never, { type: 'server', message: e.message }));
        return;
      }
      if (v.missingConfirmations.length) {
        const list = v.confirmations.filter((c) => v.missingConfirmations.includes(`${c.key}:${c.rowIndex}`));
        setPending({ list, answers });
        return;
      }
      if (v.approval === '否認') {
        setDenial(v.denialReasons);
        return;
      }
      const res = await save.mutateAsync(input);
      toast.success(isNew ? '事前連絡を登録しました' : '事前連絡を更新しました', `連絡書番号 ${res.noticeNo}`);
      navigate(yearPath(`advance-notices/${res.id}`));
    } catch (e) {
      if (e instanceof ApiError && e.code === 'APPROVAL_DENIED') setDenial((e.details as ValidateResult['denialReasons']) ?? []);
      else if (!applyApiErrors(e, setError)) toast.error('保存できませんでした', errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = handleSubmit((values) => run(values, {}));
  const listPath = detail ? yearPath(`advance-notices/${detail.id}`) : yearPath('advance-notices');
  const onCancel = async () => {
    if (formState.isDirty && !(await confirm({ title: '入力内容を破棄しますか？', message: '保存していない変更は失われます。', confirmLabel: '破棄して戻る', tone: 'danger' }))) return;
    navigate(listPath);
  };

  return (
    <form onSubmit={onSubmit} noValidate>
      <PageHeader
        eyebrow="日々の報告 ／ 事前連絡"
        title={isNew ? '事前連絡 新規登録' : '事前連絡 変更・中止'}
        meta={detail && <StatusBadge tone="navy">{detail.noticeNo}</StatusBadge>}
        description="外国漁船操業等調査・監視に係る作業実施船の事前連絡（別記様式第4号）。保存時に承認判定を行い、否認の場合は登録できません。"
      />
      <IssueList tone="error" title="入力内容を確認してください" items={issues} rowLabel={(i) => `${i + 1}行目`} />

      <Panel title="報告者">
        <FormGrid columns={4}>
          <FormField label="事業実施者" required>
            <Controller
              control={control}
              name="businessOperatorId"
              render={({ field }) => (
                <BusinessOperatorSelect aria-label="事業実施者" fiscalYear={year} value={field.value} onChange={field.onChange} placeholder="選択してください" disabled={!isNew} width="full" />
              )}
            />
          </FormField>
          <FormField label="報告者名" required error={formState.errors.reporterName?.message}>
            <TextField {...register('reporterName')} />
          </FormField>
          <FormField label="報告日" required error={formState.errors.reportDate?.message}>
            <DateField {...register('reportDate')} />
          </FormField>
          {!isNew && (
            <FormField label="登録区分" required>
              <Controller
                control={control}
                name="registrationType"
                render={({ field }) => (
                  <RadioGroup name={field.name} value={field.value ?? '変更'} onChange={field.onChange} options={[{ value: '変更', label: '変更' }, { value: '中止', label: '中止' }]} />
                )}
              />
            </FormField>
          )}
          {!isNew && (
            <FormField label="変更理由" span={4} hint={regType === '中止' ? '中止の場合は必須です' : '変更区分「変更」の行がある場合は必須です'}>
              <Textarea rows={2} {...register('changeReason')} />
            </FormField>
          )}
        </FormGrid>
      </Panel>

      <Panel
        title="実施船"
        description="実施船は2隻以上必要です。名簿外の船（通常操業船・他漁協の船）は「選択肢に無い船名」に船名と所属漁協名を入力してください。"
        actions={
          <Button size="sm" icon={<Plus size={15} />} onClick={() => rows.append(emptyVesselRow())}>
            実施船を追加
          </Button>
        }
      >
        <div className="bs-notice-rows">
          {rows.fields.map((f, index) => (
            <VesselRowCard
              key={f.id}
              index={index}
              control={control}
              register={register}
              setValue={setValue}
              isNew={isNew}
              headerOp={headerOp}
              year={year}
              onCopy={() => {
                const v = form.getValues(`vessels.${index}`);
                rows.insert(index + 1, { ...v, rowKey: null, saved: false, savedDeleted: false, changeType: null, vesselId: null, unlisted: false, unlistedVesselName: null, phone: '' });
              }}
              onRemove={() => rows.remove(index)}
              errors={formState.errors.vessels?.[index] as Record<string, { message?: string }> | undefined}
            />
          ))}
        </div>
      </Panel>

      <FormActions>
        <Button onClick={() => void onCancel()}>キャンセル</Button>
        <Button type="submit" variant="primary" icon={<Save size={16} />} loading={busy}>
          {isNew ? '登録する' : '保存する'}
        </Button>
      </FormActions>

      <ConfirmationModal
        pending={pending}
        onCancel={() => setPending(null)}
        onSubmit={(answers) => {
          setPending(null);
          void handleSubmit((values) => run(values, answers))();
        }}
      />
      <Modal
        open={denial != null}
        onClose={() => setDenial(null)}
        title="申し訳ありませんが、承認できません"
        size="md"
        footer={<Button variant="primary" onClick={() => setDenial(null)}>閉じる</Button>}
      >
        <p style={{ marginTop: 0 }}>以下の理由により、この内容では事前連絡を登録できません。出港日時・実施船・実施海区などを見直してください。</p>
        <IssueList tone="error" title="否認理由" items={(denial ?? []).map((d) => ({ message: d.message }))} />
      </Modal>
    </form>
  );
}

/** 確認（作業時間 8 時間未満・出港済み）を YES/NO で回答させる */
function ConfirmationModal({
  pending,
  onCancel,
  onSubmit,
}: {
  pending: { list: Confirmation[]; answers: Record<string, boolean> } | null;
  onCancel: () => void;
  onSubmit: (answers: Record<string, boolean>) => void;
}) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  if (!pending) return null;
  const keyOf = (c: Confirmation) => `${c.key}:${c.rowIndex}`;
  const label = (c: Confirmation) =>
    c.key === 'DEPARTED_ALREADY'
      ? [
          { value: 'yes', label: 'はい、出港しました。' },
          { value: 'no', label: 'いいえ、まだ出港していません。' },
        ]
      : [
          { value: 'yes', label: 'YES' },
          { value: 'no', label: 'NO' },
        ];
  const complete = pending.list.every((c) => answers[keyOf(c)]);
  return (
    <Modal
      open
      onClose={onCancel}
      title={
        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          <ShieldAlert size={18} /> 確認してください
        </span>
      }
      size="md"
      footer={
        <>
          <Button onClick={onCancel}>キャンセル</Button>
          <Button
            variant="primary"
            disabled={!complete}
            onClick={() => {
              const next = { ...pending.answers };
              pending.list.forEach((c) => (next[keyOf(c)] = answers[keyOf(c)] === 'yes'));
              setAnswers({});
              onSubmit(next);
            }}
          >
            回答して続ける
          </Button>
        </>
      }
    >
      <ul className="bs-confirm-list">
        {pending.list.map((c) => (
          <li key={keyOf(c)}>
            <div className="bs-confirm-list__row">{c.rowIndex + 1}行目</div>
            <p className="bs-confirm-list__msg">{c.message}</p>
            <div className="bs-confirm-list__choices" role="group" aria-label={`${c.rowIndex + 1}行目の回答`}>
              {label(c).map((o) => (
                <Button
                  key={o.value}
                  size="sm"
                  variant={answers[keyOf(c)] === o.value ? (o.value === 'yes' ? 'primary' : 'danger') : 'secondary'}
                  aria-pressed={answers[keyOf(c)] === o.value}
                  onClick={() => setAnswers((a) => ({ ...a, [keyOf(c)]: o.value }))}
                >
                  {o.label}
                </Button>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </Modal>
  );
}

function VesselRowCard({
  index,
  control,
  register,
  setValue,
  isNew,
  headerOp,
  year,
  onCopy,
  onRemove,
  errors,
}: {
  index: number;
  control: Control<NoticeForm>;
  register: UseFormRegister<NoticeForm>;
  setValue: UseFormSetValue<NoticeForm>;
  isNew: boolean;
  headerOp: number | null;
  year: number;
  onCopy: () => void;
  onRemove: () => void;
  errors?: Record<string, { message?: string }>;
}) {
  const { data: codes } = useCodeValues();
  const p = `vessels.${index}` as const;
  const row = useWatch({ control, name: p });
  const deleted = row.changeType === '削除';
  const err = (k: string) => errors?.[k]?.message;
  const user = useCurrentUser();
  const vesselOp = user.organizationType === '漁協' && user.businessOperatorId != null ? Number(user.businessOperatorId) : null;
  return (
    <section className={deleted ? 'bs-notice-row is-deleted' : 'bs-notice-row'} aria-label={`${index + 1}行目の実施船`}>
      <header className="bs-notice-row__head">
        <span className="bs-notice-row__no">No.{String(index + 1).padStart(2, '0')}</span>
        <span className="bs-notice-row__title">{row.unlisted ? row.unlistedVesselName || '（選択肢に無い船）' : '実施船'}</span>
        {row.saved ? <StatusBadge tone={deleted ? 'danger' : 'neutral'}>{deleted ? changeTypeLabel('削除') : '登録済'}</StatusBadge> : <StatusBadge tone="info">追加</StatusBadge>}
        <div className="bs-notice-row__tools">
          <Controller
            control={control}
            name={`${p}.unlisted`}
            render={({ field }) => (
              <Checkbox className="bs-unlisted-toggle" label="選択肢に無い船名" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={row.saved || deleted} />
            )}
          />
          <IconButton label="行をコピー" icon={<Copy size={15} />} onClick={onCopy} disabled={deleted} />
          {row.saved ? (
            <Controller
              control={control}
              name={`${p}.changeType`}
              render={({ field }) => (
                <IconButton
                  label={deleted ? '中止を取り消す' : 'この実施船を中止（変更区分「中止」）'}
                  icon={deleted ? <RotateCcw size={15} /> : <Trash2 size={15} />}
                  variant={deleted ? 'ghost' : 'danger'}
                  onClick={() => field.onChange(deleted ? null : '削除')}
                />
              )}
            />
          ) : (
            <IconButton label="行を削除" icon={<Trash2 size={15} />} variant="danger" onClick={onRemove} />
          )}
        </div>
      </header>
      <div className="bs-notice-row__body">
        {row.unlisted ? (
          <>
            <FormField label="選択肢に無い船名（船名＋所属漁協名）" required className="is-w4" error={err('unlistedVesselName')}>
              <TextField placeholder="例）第一○○丸（本部漁業協同組合）" {...register(`${p}.unlistedVesselName`)} />
            </FormField>
            <FormField label="選択肢に無い船名の追加理由" className="is-w4">
              <TextField {...register(`${p}.unlistedReason`)} />
            </FormField>
          </>
        ) : (
          <FormField label="実施船名" required className="is-w4" error={err('vesselId')}>
            <Controller
              control={control}
              name={`${p}.vesselId`}
              render={({ field }) => (
                <VesselSelect
                  aria-label="実施船名"
                  fiscalYear={year}
                  businessOperatorId={vesselOp}
                  value={field.value}
                  disabled={deleted}
                  width="full"
                  onChange={(v, opt) => {
                    field.onChange(v);
                    if (opt) {
                      setValue(`${p}.businessOperatorId`, opt.businessOperatorId);
                      if (!row.phone) setValue(`${p}.phone`, opt.phone ?? '');
                    }
                  }}
                />
              )}
            />
          </FormField>
        )}
        <FormField label="電話番号" className="is-w2">
          <TextField {...register(`${p}.phone`)} disabled={deleted} />
        </FormField>
        <FormField label="乗組員数" required className="is-w2" error={err('crewCount')}>
          <Controller control={control} name={`${p}.crewCount`} render={({ field }) => <NumberField {...field} unit="人" width="full" disabled={deleted} aria-label="乗組員数" />} />
        </FormField>
        <FormField label="港名" required className={row.unlisted ? 'is-w2' : 'is-w4'} error={err('portName')}>
          <Controller
            control={control}
            name={`${p}.portName`}
            render={({ field }) => (
              <PortSelect aria-label="港名" fiscalYear={year} businessOperatorId={row.businessOperatorId ?? headerOp} value={field.value} onChange={field.onChange} allowFreeText width="full" disabled={deleted} />
            )}
          />
        </FormField>
        <FormField label="出港日時" required className="is-w6" error={err('departureDate') ?? err('departureHour') ?? err('departureMinute')}>
          <DateTimeInput control={control} register={register} base={p} kind="departure" disabled={deleted} />
        </FormField>
        <FormField label="入港日時" required className="is-w6" error={err('arrivalDate') ?? err('arrivalHour') ?? err('arrivalMinute')}>
          <DateTimeInput control={control} register={register} base={p} kind="arrival" disabled={deleted} />
        </FormField>
        <FormField label="実施海区（農林漁区番号）" required className="is-w4" hint="3桁の番号をピリオド区切り（例 811.520）" error={err('zoneNumbers')}>
          <TextField className="bs-num" {...register(`${p}.zoneNumbers`)} disabled={deleted} />
        </FormField>
        <FormField label="漁獲調査・販売" className="is-w4">
          <div className="bs-dt">
            <Controller control={control} name={`${p}.catchSurveyPlanned`} render={({ field }) => <Checkbox label="漁獲調査予定あり" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={deleted} />} />
            <Controller control={control} name={`${p}.catchSalePlanned`} render={({ field }) => <Checkbox label="漁獲物販売予定あり" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={deleted} />} />
          </div>
        </FormField>
        <FormField label="備考" className="is-w4">
          <TextField {...register(`${p}.note`)} disabled={deleted} />
        </FormField>
        {!isNew && (
          <div className="bs-notice-row__reasons">
            <FormField label="実施船変更理由">
              <ControlledSelect control={control} name={`${p}.vesselChangeReason`} placeholder="—" options={options(codes?.vesselChangeReasons)} />
            </FormField>
            <FormField label="出港日変更理由" error={err('departureChangeReason')}>
              <ControlledSelect control={control} name={`${p}.departureChangeReason`} placeholder="—" options={options(codes?.departureChangeReasons)} />
            </FormField>
            <FormField label="入港日変更理由" error={err('arrivalChangeReason')}>
              <ControlledSelect control={control} name={`${p}.arrivalChangeReason`} placeholder="—" options={options(codes?.arrivalChangeReasons)} />
            </FormField>
            <FormField label="電話番号変更理由">
              <ControlledSelect control={control} name={`${p}.phoneChangeReason`} placeholder="—" options={options(codes?.vesselChangeReasons)} />
            </FormField>
            <FormField label="乗組員変更理由">
              <ControlledSelect control={control} name={`${p}.crewChangeReason`} placeholder="—" options={options(codes?.vesselChangeReasons)} />
            </FormField>
            <FormField label="港名変更理由">
              <ControlledSelect control={control} name={`${p}.portChangeReason`} placeholder="—" options={options(codes?.vesselChangeReasons)} />
            </FormField>
            <FormField label="実施海域変更理由" className="is-w4">
              <TextField {...register(`${p}.zoneChangeReason`)} />
            </FormField>
          </div>
        )}
      </div>
    </section>
  );
}

function DateTimeInput({
  control,
  register,
  base,
  kind,
  disabled,
}: {
  control: Control<NoticeForm>;
  register: UseFormRegister<NoticeForm>;
  base: `vessels.${number}`;
  kind: 'departure' | 'arrival';
  disabled?: boolean;
}) {
  return (
    <div className="bs-dt">
      <DateField {...register(`${base}.${kind}Date`)} disabled={disabled} aria-label={kind === 'departure' ? '出港日' : '入港日'} />
      <span className="bs-dt__hm">
        <Controller control={control} name={`${base}.${kind}Hour`} render={({ field }) => <NumberField {...field} width="xs" disabled={disabled} aria-label="時" />} />
        時
        <Controller control={control} name={`${base}.${kind}Minute`} render={({ field }) => <NumberField {...field} width="xs" disabled={disabled} aria-label="分" />} />
        分
      </span>
    </div>
  );
}
